import * as React from 'react';
import clsx from 'clsx';
import { ToggleSwitch } from '@/components/ui/ToggleSwitch';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/context/ToastContext';
import { getErrorMessage } from '@/api/api';
import { userService } from '@/services/userService';
import { SettingsHeader } from './shared/SettingsHeader';
import bellIcon from '@/assets/images/svg/bell_black_icon.svg';

export const NotificationSettings: React.FC = () => {
  const { user, updateUser } = useAuth();
  const { addToast } = useToast();
  const [isSaving, setIsSaving] = React.useState(false);

  const isEnabled = user?.reengagement_notifications_enabled ?? true;

  const handleToggle = async (nextValue: boolean) => {
    if (!user || isSaving) return;

    setIsSaving(true);
    updateUser({ ...user, reengagement_notifications_enabled: nextValue });

    try {
      await userService.updateMe({ reengagement_notifications_enabled: nextValue });
      addToast({
        title: nextValue ? 'Subscribed' : 'Unsubscribed',
        subtitle: nextValue
          ? 'You will receive email notifications when your Echoes miss you.'
          : 'You will no longer receive email notifications.',
        type: 'success',
      });
    } catch (error) {
      updateUser({ ...user, reengagement_notifications_enabled: !nextValue });
      addToast({
        title: 'Update Failed',
        subtitle: getErrorMessage(error),
        type: 'error',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div
      className={clsx(
        'relative flex w-full',
        'flex-col items-start gap-6',
        'md:flex-row md:items-center md:justify-between md:gap-6'
      )}
    >
      <div className='min-w-0 flex-1'>
        <SettingsHeader
          icon={bellIcon}
          title='Email notifications'
          description='Receive occasional emails when your Echoes miss you. Turn off to unsubscribe.'
        />
      </div>

      <div className='shrink-0 md:w-auto'>
        <ToggleSwitch
          checked={isEnabled}
          onChange={handleToggle}
          disabled={isSaving || !user}
          ariaLabel='Toggle email notifications'
        />
      </div>
    </div>
  );
};
